import { mapLocalePreferenceToIntlLocale } from './friendlyDateFormatterIntl';
import {
    CalendarDateFormatPreset,
    CALENDAR_DATE_FORMAT_PRESETS,
    getCalendarDateFormatOptions,
} from './intlDateFormatPresets';

type FormatCalendarDateArgs = {
    /**
     * Timestamp in milliseconds (JavaScript standard) or a Date object.
     *
     * Note: backend systems (like Stripe) return timestamps in Unix seconds.
     * Convert to milliseconds before passing to this function:
     * `formatCalendarDate({ timestamp: unixSeconds * 1000, ... })`
     */
    timestamp: number | Date;
    localePreference?: string;
    preset: CalendarDateFormatPreset;
};

const formatLocalizedDate = ({ timestamp, localePreference, preset }: FormatCalendarDateArgs): string => {
    const date = new Date(timestamp);

    // Guard against invalid or missing upstream timestamps to avoid Intl throwing.
    if (Number.isNaN(date.getTime())) return '';

    const intlLocale = mapLocalePreferenceToIntlLocale(localePreference);
    const options = getCalendarDateFormatOptions({ preset });

    return new Intl.DateTimeFormat(intlLocale, options).format(date);
};

/**
 * Formats a calendar date using locale-aware Intl.DateTimeFormat.
 *
 * @param timestamp - Date in milliseconds (JavaScript standard) or Date object
 * @param localePreference - User's locale preference (e.g., 'en-au', 'es-es')
 * @param preset - Format preset ('long_with_weekday' or 'long')
 * @returns Formatted date string (e.g., "Friday, 15 March 2024" for long_with_weekday)
 *
 * @example
 * // Stripe timestamps are in seconds, so multiply by 1000
 * const stripeTimestamp = 1729382400; // Unix seconds from Stripe API
 * formatCalendarDate({
 *   timestamp: stripeTimestamp * 1000,
 *   localePreference: 'en-au',
 *   preset: CALENDAR_DATE_FORMAT_PRESETS.LONG,
 * });
 */
export const formatCalendarDate = ({ timestamp, localePreference, preset }: FormatCalendarDateArgs): string =>
    formatLocalizedDate({ timestamp, localePreference, preset });

export { CALENDAR_DATE_FORMAT_PRESETS };
